import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Plus, Trash2, X, ChevronLeft, ChevronRight, Pin } from 'lucide-react';
import { toast } from 'sonner';
import { FamilyProductFields } from '../../components/admin/FamilyProductFields';
import { brandsApi, brandNewsApi, brandProductsApi, brandProjectsApi, catalogueApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import ImageUpload from '../../components/admin/ImageUpload';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

const AdminBrandEdit = () => {
  const { brandId } = useParams();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [featuresText, setFeaturesText] = useState('');

  // Modal states
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showCatalogueModal, setShowCatalogueModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [modalSaving, setModalSaving] = useState(false);

  // Catalogue state
  const [catalogueProducts, setCatalogueProducts] = useState([]);
  const [cataloguePage, setCataloguePage] = useState(1);
  const [catalogueTotal, setCatalogueTotal] = useState(0);
  const [catalogueTotalPages, setCatalogueTotalPages] = useState(0);
  const [catalogueSearch, setCatalogueSearch] = useState('');

  useEffect(() => {
    const fetchBrand = async () => {
      try {
        const response = await brandsApi.get(brandId);
        setBrand(response.data);
        setFeaturesText(response.data.features?.join('\n') || '');
      } catch (error) {
        console.error('Error fetching brand:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBrand();
  }, [brandId]);

  // Fetch catalogue products when brand has tabCatalogue
  useEffect(() => {
    if (!brand?.tabCatalogue) return;
    const fetchCatalogue = async () => {
      try {
        const res = await catalogueApi.getAll(brandId, { page: cataloguePage, limit: 20, search: catalogueSearch });
        setCatalogueProducts(res.data.products || []);
        setCatalogueTotal(res.data.total || 0);
        setCatalogueTotalPages(res.data.totalPages || 0);
      } catch (error) {
        console.error('Error fetching catalogue:', error);
      }
    };
    fetchCatalogue();
  }, [brand?.tabCatalogue, brandId, cataloguePage, catalogueSearch]);

  const handleChange = (field, value) => {
    setBrand((prev) => ({ ...prev, [field]: value }));
  };

  const handleFeaturesChange = (value) => {
    setFeaturesText(value);
    const features = value.split('\n').filter((f) => f.trim());
    setBrand((prev) => ({ ...prev, features }));
  };

  const handleSaveBrand = async () => {
    setSaving(true);
    setMessage('');
    try {
      const { news, products, projects, ...brandData } = brand;
      await brandsApi.update(brandId, brandData);
      setMessage('Brand saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Error saving brand');
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  // News handlers
  const handleSaveNews = async (newsData) => {
    setModalSaving(true);
    try {
      if (editingItem?.id) {
        await brandNewsApi.update(brandId, editingItem.id, newsData);
      } else {
        await brandNewsApi.create(brandId, newsData);
      }
      const response = await brandsApi.get(brandId);
      setBrand(response.data);
      setShowNewsModal(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving news:', error);
    } finally {
      setModalSaving(false);
    }
  };

  const handleDeleteNews = async (newsId) => {
    if (!window.confirm('Are you sure you want to delete this news item?')) return;
    try {
      await brandNewsApi.delete(brandId, newsId);
      const response = await brandsApi.get(brandId);
      setBrand(response.data);
    } catch (error) {
      console.error('Error deleting news:', error);
    }
  };

  // Product handlers
  const handleSaveProduct = async (productData) => {
    setModalSaving(true);
    try {
      if (editingItem?.id) {
        await brandProductsApi.update(brandId, editingItem.id, productData);
      } else {
        await brandProductsApi.create(brandId, productData);
      }
      const response = await brandsApi.get(brandId);
      setBrand(response.data);
      setShowProductModal(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving product:', error);
    } finally {
      setModalSaving(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await brandProductsApi.delete(brandId, productId);
      const response = await brandsApi.get(brandId);
      setBrand(response.data);
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  // Project handlers
  const handleSaveProject = async (projectData) => {
    setModalSaving(true);
    try {
      if (editingItem?.id) {
        await brandProjectsApi.update(brandId, editingItem.id, projectData);
      } else {
        await brandProjectsApi.create(brandId, projectData);
      }
      const response = await brandsApi.get(brandId);
      setBrand(response.data);
      setShowProjectModal(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving project:', error);
    } finally {
      setModalSaving(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await brandProjectsApi.delete(brandId, projectId);
      const response = await brandsApi.get(brandId);
      setBrand(response.data);
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  // Catalogue handlers
  const handleSaveCatalogueProduct = async (productData) => {
    setModalSaving(true);
    try {
      if (editingItem?.id) {
        await catalogueApi.update(brandId, editingItem.id, productData);
      } else {
        await catalogueApi.create(brandId, productData);
      }
      // Refresh catalogue
      const res = await catalogueApi.getAll(brandId, { page: cataloguePage, limit: 20, search: catalogueSearch });
      setCatalogueProducts(res.data.products || []);
      setCatalogueTotal(res.data.total || 0);
      setCatalogueTotalPages(res.data.totalPages || 0);
      setShowCatalogueModal(false);
      setEditingItem(null);
      toast.success('Product saved');
    } catch (error) {
      console.error('Error saving catalogue product:', error);
      toast.error(error.response?.data?.detail?.toString() || 'Could not save the product. Please try again.');
    } finally {
      setModalSaving(false);
    }
  };

  const handleDeleteCatalogueProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await catalogueApi.delete(brandId, productId);
      const res = await catalogueApi.getAll(brandId, { page: cataloguePage, limit: 20, search: catalogueSearch });
      setCatalogueProducts(res.data.products || []);
      setCatalogueTotal(res.data.total || 0);
      setCatalogueTotalPages(res.data.totalPages || 0);
    } catch (error) {
      console.error('Error deleting catalogue product:', error);
    }
  };

  const handleTogglePin = async (productId) => {
    try {
      await catalogueApi.togglePin(brandId, productId);
      const res = await catalogueApi.getAll(brandId, { page: cataloguePage, limit: 20, search: catalogueSearch });
      setCatalogueProducts(res.data.products || []);
    } catch (error) {
      console.error('Error toggling pin:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!brand) {
    return <div>Brand not found</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <Link to="/admin/brands" className="mr-4 text-gray-400 hover:text-gray-600">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-2xl font-semibold text-gray-900">Edit {brand.name}</h1>
        </div>
        {message && (
          <span className={`text-sm ${message.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>
            {message}
          </span>
        )}
      </div>

      <Tabs defaultValue="info" className="space-y-6">
        <TabsList>
          <TabsTrigger value="info">Brand Info</TabsTrigger>
          <TabsTrigger value="news">News ({brand.news?.length || 0})</TabsTrigger>
          {brand.tabBestOf !== false && (
            <TabsTrigger value="products">Best Of ({brand.products?.length || 0})</TabsTrigger>
          )}
          {brand.tabCatalogue && (
            <TabsTrigger value="catalogue">Catalogue ({catalogueTotal})</TabsTrigger>
          )}
          <TabsTrigger value="projects">Projects ({brand.projects?.length || 0})</TabsTrigger>
        </TabsList>

        {/* Brand Info Tab */}
        <TabsContent value="info">
          <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name">Brand Name</Label>
                <Input
                  id="name"
                  value={brand.name || ''}
                  onChange={(e) => handleChange('name', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={brand.country || ''}
                  onChange={(e) => handleChange('country', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="specialty">Specialty</Label>
                <Input
                  id="specialty"
                  value={brand.specialty || ''}
                  onChange={(e) => handleChange('specialty', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">Website URL</Label>
                <Input
                  id="website"
                  value={brand.website || ''}
                  onChange={(e) => handleChange('website', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <ImageUpload
                  label="Logo"
                  value={brand.logo || ''}
                  onChange={(url) => handleChange('logo', url)}
                  previewHeight="h-20"
                  placeholder="https://brand-logo.png"
                />
              </div>
              <div className="space-y-2">
                <ImageUpload
                  label="Hero Image"
                  value={brand.heroImage || ''}
                  onChange={(url) => handleChange('heroImage', url)}
                  previewHeight="h-32"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="about">About (Full Description)</Label>
              <Textarea
                id="about"
                rows={6}
                value={brand.about || ''}
                onChange={(e) => handleChange('about', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="features">Features (one per line)</Label>
              <Textarea
                id="features"
                rows={5}
                value={featuresText}
                onChange={(e) => handleFeaturesChange(e.target.value)}
                placeholder="Feature 1\nFeature 2\nFeature 3"
              />
            </div>
            <div className="space-y-2 border-t pt-4">
              <Label htmlFor="projectsPageUrl">Projects Page URL</Label>
              <Input
                id="projectsPageUrl"
                value={brand.projectsPageUrl || ''}
                onChange={(e) => handleChange('projectsPageUrl', e.target.value)}
                placeholder="https://www.brand.com/projects"
              />
              <p className="text-xs text-gray-500">
                Custom URL for the "View Projects" button on the brand page. Leave empty to use default ({brand.website}/projects).
              </p>
            </div>
            <div className="flex justify-end">
              <Button onClick={handleSaveBrand} disabled={saving}>
                {saving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                ) : (
                  <><Save className="h-4 w-4 mr-2" />Save Brand Info</>
                )}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* News Tab */}
        <TabsContent value="news">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-medium">Brand News</h2>
              <Button onClick={() => { setEditingItem(null); setShowNewsModal(true); }}>
                <Plus className="h-4 w-4 mr-2" />Add News
              </Button>
            </div>
            <div className="space-y-4">
              {brand.news?.map((item) => (
                <div key={item.id} className="flex items-center border rounded-lg p-4">
                  {item.image && (
                    <img src={item.image} alt={item.title} className="w-20 h-20 object-cover rounded mr-4" />
                  )}
                  <div className="flex-1">
                    <h3 className="font-medium">{item.title}</h3>
                    <p className="text-sm text-gray-500">{item.date}</p>
                    <p className="text-sm text-gray-600 line-clamp-1">{item.excerpt}</p>
                  </div>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" onClick={() => { setEditingItem(item); setShowNewsModal(true); }}>
                      Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDeleteNews(item.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
              {!brand.news?.length && <p className="text-gray-500 text-center py-8">No news items yet</p>}
            </div>
          </div>
        </TabsContent>

        {/* Products Tab */}
        {brand.tabBestOf !== false && (
        <TabsContent value="products">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-medium">Best Of Products</h2>
              <Button onClick={() => { setEditingItem(null); setShowProductModal(true); }}>
                <Plus className="h-4 w-4 mr-2" />Add Product
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {brand.products?.map((item) => (
                <div key={item.id} className="flex items-center border rounded-lg p-4">
                  {item.image && (
                    <img src={item.image} alt={item.name} className="w-20 h-20 object-cover rounded mr-4" />
                  )}
                  <div className="flex-1">
                    <h3 className="font-medium">{item.name}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2">{item.description}</p>
                  </div>
                  <div className="flex flex-col space-y-2">
                    <Button variant="outline" size="sm" onClick={() => { setEditingItem(item); setShowProductModal(true); }}>
                      Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDeleteProduct(item.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
              {!brand.products?.length && <p className="text-gray-500 text-center py-8 col-span-2">No products yet</p>}
            </div>
          </div>
        </TabsContent>
        )}

        {/* Catalogue Tab */}
        {brand.tabCatalogue && (
        <TabsContent value="catalogue">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-medium">Catalogue Products ({catalogueTotal})</h2>
              <Input
                value={catalogueSearch}
                onChange={(e) => { setCatalogueSearch(e.target.value); setCataloguePage(1); }}
                placeholder="Search name, family, code, finish..."
                className="max-w-xs mx-4"
                data-testid="admin-catalogue-search"
              />
              <Button onClick={() => { setEditingItem(null); setShowCatalogueModal(true); }}>
                <Plus className="h-4 w-4 mr-2" />Add Product
              </Button>
            </div>
            <div className="space-y-2">
              {catalogueProducts.map((item) => (
                <div key={item.id} className={`flex items-center border rounded-lg p-3 ${item.pinned ? 'border-amber-300 bg-amber-50' : ''}`}>
                  {item.mainImage && (
                    <img src={item.mainImage} alt={item.name} className="w-14 h-14 object-contain bg-gray-50 rounded mr-4 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-gray-500">{item.code}</span>
                      <span className="text-xs px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">{item.category}</span>
                      {item.family && <span className="text-xs px-1.5 py-0.5 bg-blue-50 rounded text-blue-700">{item.family}</span>}
                      {item.variants?.length > 0 && <span className="text-xs text-gray-500">{item.variants.length} variants</span>}
                      {item.pinned && <span className="text-xs px-1.5 py-0.5 bg-amber-200 text-amber-800 rounded font-medium">Pinned</span>}
                    </div>
                    <h3 className="font-medium truncate">{item.family ? `${item.name} ${item.productType?.toLowerCase() || ''}` : `"${item.name}"`}</h3>
                  </div>
                  <div className="flex space-x-2 flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTogglePin(item.id)}
                      title={item.pinned ? 'Unpin' : 'Pin to top'}
                      data-testid={`admin-catalogue-pin-${item.code}`}
                    >
                      <Pin className={`h-4 w-4 ${item.pinned ? 'text-amber-500' : 'text-gray-400'}`} />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setEditingItem(item); setShowCatalogueModal(true); }} data-testid={`admin-catalogue-edit-${item.code}`}>
                      Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDeleteCatalogueProduct(item.id)} data-testid={`admin-catalogue-delete-${item.code}`}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
              {catalogueProducts.length === 0 && <p className="text-gray-500 text-center py-8">No catalogue products yet</p>}
            </div>
            {/* Pagination */}
            {catalogueTotalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t">
                <Button variant="outline" size="sm" disabled={cataloguePage === 1} onClick={() => setCataloguePage(p => p - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-gray-600">Page {cataloguePage} of {catalogueTotalPages}</span>
                <Button variant="outline" size="sm" disabled={cataloguePage === catalogueTotalPages} onClick={() => setCataloguePage(p => p + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </TabsContent>
        )}

        {/* Projects Tab */}
        <TabsContent value="projects">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-medium mb-4">Project Gallery Link</h2>
            <p className="text-gray-600 mb-6">
              Configure where the "View Projects" button links to on the public brand page.
            </p>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="projectsUrl">Projects Page URL</Label>
                <Input
                  id="projectsUrl"
                  value={brand.projectsPageUrl || ''}
                  onChange={(e) => handleChange('projectsPageUrl', e.target.value)}
                  placeholder={brand.website ? `${brand.website}/projects` : 'https://www.brand.com/projects'}
                />
              </div>
              <p className="text-sm text-gray-500">
                Leave empty to use the default URL: <span className="font-mono text-xs">{brand.website}/projects</span>
              </p>
              <div className="pt-4">
                <Button onClick={handleSaveBrand} disabled={saving}>
                  {saving ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                  ) : (
                    <><Save className="h-4 w-4 mr-2" />Save Projects URL</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* News Modal */}
      <ItemModal
        open={showNewsModal}
        onClose={() => { setShowNewsModal(false); setEditingItem(null); }}
        title={editingItem ? 'Edit News' : 'Add News'}
        item={editingItem}
        onSave={handleSaveNews}
        saving={modalSaving}
        fields={[
          { name: 'title', label: 'Title', type: 'text' },
          { name: 'date', label: 'Date', type: 'text' },
          { name: 'excerpt', label: 'Excerpt', type: 'textarea' },
          { name: 'image', label: 'Image URL', type: 'text' },
        ]}
      />

      {/* Product Modal */}
      <ItemModal
        open={showProductModal}
        onClose={() => { setShowProductModal(false); setEditingItem(null); }}
        title={editingItem ? 'Edit Product' : 'Add Product'}
        item={editingItem}
        onSave={handleSaveProduct}
        saving={modalSaving}
        fields={[
          { name: 'name', label: 'Name', type: 'text' },
          { name: 'description', label: 'Description', type: 'textarea' },
          { name: 'image', label: 'Image URL', type: 'text' },
          { name: 'link', label: 'Link URL', type: 'text', section: 'link' },
          { name: 'linkText', label: 'Button Text', type: 'text', section: 'link' },
        ]}
      />

      {/* Catalogue Product Modal */}
      <CatalogueProductModal
        open={showCatalogueModal}
        onClose={() => { setShowCatalogueModal(false); setEditingItem(null); }}
        item={editingItem}
        onSave={handleSaveCatalogueProduct}
        saving={modalSaving}
        familyMode={brand?.catalogueMode === 'families'}
      />
    </div>
  );
};

// Generic Item Modal Component
const ItemModal = ({ open, onClose, title, item, onSave, saving, fields }) => {
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (item) {
      setFormData(item);
    } else {
      setFormData({});
    }
  }, [item, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  // Separate regular fields from link section fields
  const regularFields = fields.filter(f => f.section !== 'link');
  const linkFields = fields.filter(f => f.section === 'link');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {regularFields.map((field) => (
            <div key={field.name} className="space-y-2">
              <Label htmlFor={field.name}>{field.label}</Label>
              {field.type === 'textarea' ? (
                <Textarea
                  id={field.name}
                  rows={3}
                  value={formData[field.name] || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, [field.name]: e.target.value }))}
                />
              ) : (
                <Input
                  id={field.name}
                  type={field.type}
                  value={formData[field.name] || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, [field.name]: e.target.value }))}
                />
              )}
            </div>
          ))}
          {formData.image && (
            <img src={formData.image} alt="Preview" className="h-24 object-cover rounded" />
          )}
          
          {/* Link Button Section */}
          {linkFields.length > 0 && (
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Link Button (Optional)</h4>
              <div className="grid grid-cols-2 gap-4">
                {linkFields.map((field) => (
                  <div key={field.name} className="space-y-2">
                    <Label htmlFor={field.name}>{field.label}</Label>
                    <Input
                      id={field.name}
                      type={field.type}
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, [field.name]: e.target.value }))}
                      placeholder={field.name === 'link' ? 'https://...' : 'e.g., View Product'}
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Add a link to make the product image clickable and display a button.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Catalogue Product Modal Component
const CATEGORY_OPTIONS = ['Address Light', 'Plaque', 'Sconce', 'Pendant', 'Post / Bollard', 'Flush'];

const CatalogueProductModal = ({ open, onClose, item, onSave, saving, familyMode }) => {
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (item) {
      setFormData({ ...item, specs: item.specs || {} });
    } else if (familyMode) {
      setFormData({ code: '', name: '', family: '', productType: 'Pendant', categories: [], description: '', mainImage: '', images: [], documents: [], variants: [] });
    } else {
      setFormData({
        code: '', name: '', category: 'Address Light', description: '', mainImage: '',
        specs: {
          wattage: '', color_temp: '2200/2400/2700/3000/4000K', voltage: '230V',
          control: 'on/off, 1-10V, DALI, DMX, wireless', cri: 'CRI>90',
          lumens: '', ip_rating: 'IP20', Dimensions: '', Mounting: '', Certification: 'UL or ETL listed, ADA compliant, CE',
          Numbers: '', Font: 'Any typeface is possible. Braille is standard but optional', Lamping: ''
        }
      });
    }
  }, [item, open, familyMode]);

  const handleSpecChange = (key, value) => {
    setFormData(prev => ({ ...prev, specs: { ...prev.specs, [key]: value } }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`${familyMode ? 'sm:max-w-4xl' : 'sm:max-w-2xl'} max-h-[90vh] overflow-y-auto`}>
        <DialogHeader>
          <DialogTitle>{item ? 'Edit Catalogue Product' : 'Add Catalogue Product'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {familyMode ? <FamilyProductFields formData={formData} setFormData={setFormData} /> : (<>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Code</Label>
              <Input value={formData.code || ''} onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))} placeholder="NL1001.01" />
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={formData.name || ''} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} placeholder="Felix" />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <select
                value={formData.category || 'Address Light'}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm"
              >
                {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea rows={3} value={formData.description || ''} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} />
          </div>
          <div className="space-y-2">
            <Label>Main Image URL</Label>
            <Input value={formData.mainImage || ''} onChange={(e) => setFormData(prev => ({ ...prev, mainImage: e.target.value }))} placeholder="/api/products/..." />
          </div>

          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Technical Specs</h4>
            <div className="grid grid-cols-2 gap-3">
              {[
                ['wattage', 'Wattage'], ['lumens', 'Lumens'], ['color_temp', 'Colour Temp'], ['voltage', 'Voltage'],
                ['control', 'Control'], ['cri', 'CRI'], ['ip_rating', 'IP Rating'], ['Dimensions', 'Dimensions'], ['Mounting', 'Mounting'],
                ['Certification', 'Certification'], ['Numbers', 'Numbers'], ['Font', 'Font']
              ].map(([key, label]) => (
                <div key={key} className="space-y-1">
                  <Label className="text-xs">{label}</Label>
                  <Input className="text-sm" value={formData.specs?.[key] || ''} onChange={(e) => handleSpecChange(key, e.target.value)} />
                </div>
              ))}
            </div>
          </div>
          </>)}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="admin-catalogue-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminBrandEdit;
